import { MapPin, Ruler, TreeDeciduous, Lock, CheckCircle, AlertTriangle, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import SellerRating from "@/components/SellerRating";

export interface ListingData {
  id: string;
  title: string;
  species: string;
  seasoning: string;
  length: string;
  split: boolean;
  price_per_stere: number;
  volume: number;
  stock?: number;
  location: string;
  image_urls: string[];
  admin_verified?: boolean;
  seller_id?: string;
  seller?: { full_name: string; admin_rating?: number } | null;
  status?: string;
  pricePerStere?: number;
  distance?: number;
  rating?: number;
  reviewCount?: number;
  sellerName?: string;
  verified?: boolean;
  adminVerified?: boolean;
  imageUrl?: string;
}

const ListingCard = ({ listing }: { listing: ListingData }) => {
  const price = listing.price_per_stere ?? listing.pricePerStere ?? 0;
  const imageUrl = listing.image_urls?.[0] || listing.imageUrl || "/placeholder.svg";
  const isVerified = listing.admin_verified ?? listing.adminVerified ?? false;
  const sellerName = (listing.seller as any)?.display_name || (listing.seller as any)?.full_name || listing.sellerName || "Vendeur";
  // Prefer actual review average (listing.rating) over admin_rating
  const sellerRating = listing.rating ?? (listing.seller as any)?.admin_rating ?? 0;
  const reviewCount = listing.reviewCount ?? 0;
  const isSoldOut = listing.status === "sold_out" || (listing.stock !== undefined && listing.stock <= 0);

  return (
    <Link
      to={`/annonce/${listing.id}`}
      className="group block bg-card rounded-lg overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={imageUrl}
          alt={`Stère de bois de chauffage ${listing.species} à ${listing.location} - ChimayBois`}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isSoldOut ? "opacity-50" : ""}`}
          loading="lazy"
          decoding="async"
          width={400}
          height={300}
        />
        {isSoldOut && (
          <Badge className="absolute top-3 left-3 bg-destructive text-destructive-foreground text-xs gap-1">
            <AlertTriangle className="h-3 w-3" /> Épuisé
          </Badge>
        )}
        {!isSoldOut && isVerified && (
          <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs gap-1">
            <CheckCircle className="h-3 w-3" /> Vérifié
          </Badge>
        )}
        <div className="absolute top-3 right-3 bg-accent text-accent-foreground px-2 py-1 rounded-md text-sm font-bold">
          {price}€/stère
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-semibold text-card-foreground mb-2 group-hover:text-primary transition-colors">
          {listing.title}
        </h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <TreeDeciduous className="h-3 w-3" />
            {listing.species}
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Ruler className="h-3 w-3" />
            {listing.length}
          </span>
          <span className="text-xs text-muted-foreground">
            {listing.seasoning}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {listing.location}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{sellerName}</span>
            <SellerRating rating={sellerRating} />
            {reviewCount > 0 && (
              <span className="text-[10px] text-muted-foreground">({reviewCount})</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
          <Package className="h-3 w-3" />
          Stock: {listing.stock ?? listing.volume} stères
          {(listing as any).min_steres > 1 && ` · Min. ${(listing as any).min_steres} stères`}
        </div>
        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
          <Lock className="h-3 w-3" />
          Contact débloqué après réservation
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;
